import { computed, makeAutoObservable, observable } from 'mobx'
import {
  DoctorRegister,
  DoctorRegisterStatus,
  DoctorRegisterStep,
  DoctorRegisterStepStatus
} from 'domain/entities/doctor-register'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { useStores } from '../../../../hooks/use-stores'
import { Profile } from 'common/enum/profile'

class NewRegistrationDetailsContext {
  @observable register: DoctorRegister | undefined

  constructor() {
    makeAutoObservable(this)
  }

  @computed get steps(): DoctorRegisterStep[] | undefined {
    return this.register?.steps
  }

  @computed get pendencies(): DoctorRegisterStep[] {
    return this.steps
      ? this.steps.filter(
          (step) => step.status === DoctorRegisterStepStatus.PENDENT
        )
      : []
  }

  @computed get toReview(): DoctorRegisterStep[] {
    if (!this.steps) return []

    return this.steps.filter((step) => {
      return step.status === DoctorRegisterStepStatus.REVIEW
    })
  }

  @computed get hasReviews(): boolean {
    return this.toReview.length > 0
  }

  //register
  addRegister(register?: DoctorRegister) {
    if (register) {
      // @ts-ignore
      this.register = { ...register }
    }
  }

  //steps
  getStep(name: DoctorRegistrationSteps) {
    return this.steps?.find((step) => step.name === name)
  }

  getDirectorSteps() {
    const stepNames = [
      DoctorRegistrationSteps.SOURCE_VALIDATION,
      DoctorRegistrationSteps.PERSONAL_DATA,
      DoctorRegistrationSteps.PROFESSIONAL_DATA,
      DoctorRegistrationSteps.DOCUMENTS_AND_ATTACHMENTS,
      DoctorRegistrationSteps.TEAMS
    ]

    if (!this.steps) return []

    return this.steps.filter((step) => {
      return step?.name && stepNames.includes(step.name)
    })
  }

  updateSteps(stepToUpdate: DoctorRegisterStep) {
    const filteredArray = this.steps?.filter(
      (step) => step.name !== stepToUpdate.name
    )

    if (this.register?.steps) {
      this.register.steps = [...(filteredArray || []), stepToUpdate]
    }
  }

  get isDirector() {
    return (
      useStores().currentAccount.getCurrentAccount().user.role ===
      Profile.DIRECTOR_DOCTOR_REGISTER
    )
  }

  get activeStatusRegister() {
    return this.register?.status
  }

  get isStepsUpdateBlocked(): boolean {
    if (this.register?.status === DoctorRegisterStatus.REGISTER_CONCLUDED) {
      return true
    }
    if (!this.isDirector) {
      return (
        this.register?.status === DoctorRegisterStatus.PENDENT ||
        this.register?.status ===
          DoctorRegisterStatus.REGISTER_SEND_TO_DIRECTOR ||
        this.register?.status ===
          DoctorRegisterStatus.APPROVED_BY_DIRECTOR_WITH_PENDENCIES ||
        this.register?.status ===
          DoctorRegisterStatus.REGISTER_RESEND_TO_DIRECTOR
      )
    } else {
      return !(
        this.register?.status ===
          DoctorRegisterStatus.REGISTER_SEND_TO_DIRECTOR ||
        this.register?.status ===
          DoctorRegisterStatus.REGISTER_RESEND_TO_DIRECTOR
      )
    }
  }

  get canSendToDirector(): boolean {
    const status = [
      DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER,
      DoctorRegisterStepStatus.APPROVED_DIRECTOR
    ]
    if (
      this.register?.status === DoctorRegisterStatus.UNREVIEWED_REGISTER ||
      this.register?.status === DoctorRegisterStatus.PENDENCY_ANSWERED ||
      this.register?.status === DoctorRegisterStatus.REVIEWED_BY_DIRECTOR
    ) {
      return !!this.steps?.every((step) => {
        return status.includes(step.status!)
      })
    }

    return false
  }

  get canApprove(): boolean {
    if (!this.register?.status) return false

    const approvedOrReprovedStatus = [
      DoctorRegisterStatus.REGISTER_CONCLUDED,
      DoctorRegisterStatus.REGISTER_REPROVED_BY_DIRECTOR,
      DoctorRegisterStatus.APPROVED_BY_DIRECTOR_WITH_PENDENCIES
    ]

    const wasApprovedOrReproved = approvedOrReprovedStatus.includes(
      this.register.status
    )

    return this.isDirector && !wasApprovedOrReproved
  }
}

export const newRegistrationDetailsContext = new NewRegistrationDetailsContext()
